import { makeStyles, Theme } from '@material-ui/core/styles';
import { FC } from 'react';

import { StatusLabelClassNames, StatusLabelStatus } from 'src/types';

const MAX_STATUS_LABEL_WIDTH = 432;
const maxPriorityTextWidth = Math.ceil(0.66 * 432);

const makeLabelTypeStyles = (theme: Theme) =>
  (Object.keys(StatusLabelStatus) as Array<keyof typeof StatusLabelStatus>)
    .map(k => ({
      [`.${StatusLabelClassNames[k]}`]: {
        backgroundColor: theme.palette.statusLabel.LabelColor[k],
        cursor: 'pointer',

        [`&.${StatusLabelClassNames.HOVER_STATE}`]: {
          '&:hover': {
            backgroundColor: theme.palette.statusLabel.HoverColor[k],

            [`& .${StatusLabelClassNames.STATUS_ICON}`]: {
              backgroundColor: theme.palette.statusLabel.HoverColor[k],
            },
          },
        },

        [`& .${StatusLabelClassNames.STATUS_ICON}`]: {
          fill: theme.palette.statusLabel.IconColor[k],
        },

        [`& .${StatusLabelClassNames.ACTION_ICON}`]: {
          fill: theme.palette.statusLabel.IconColor[k],
        },

        ...(k === 'ACTION' && {
          [`& .${StatusLabelClassNames.LABEL}`]: {
            color: theme.palette.common.white,
          },

          [`& .${StatusLabelClassNames.DOT}`]: {
            fill: theme.palette.common.white,
          },
        }),
      },
    }))
    .reduce((a, b) => ({ ...a, ...b }));

const makeLabelStyles = (theme: Theme) => ({
  [`.${StatusLabelClassNames.LABEL}`]: {
    color: theme.palette.common.gray800,
  },
  ...makeLabelTypeStyles(theme),
});

const rootGridTemplateAreas = [
  StatusLabelClassNames.STATUS_ICON,
  StatusLabelClassNames.PRIMARY_LABEL_TEXT,
  StatusLabelClassNames.DOT,
  StatusLabelClassNames.PRIORITY_LABEL_TEXT,
  StatusLabelClassNames.ACTION_ICON,
];

const makeRootGridElementMargins = (theme: Theme) => ({
  '&:not(:empty)': {
    '&:not(:first-child)': { marginLeft: theme.spacing(0.5) },
  },
});

const makeRootGridTemplateAreaStyles = (theme: Theme) =>
  rootGridTemplateAreas
    .map(gridArea => ({
      [`& .${gridArea}`]: { gridArea, ...makeRootGridElementMargins(theme) },
    }))
    .reduce((a, b) => ({ ...a, ...b }));

const makeRootGrid = (theme: Theme) => ({
  [`.${StatusLabelClassNames.ROOT}`]: {
    alignItems: 'center',
    borderRadius: theme.misc.borderRadius,
    boxSizing: 'border-box',
    display: 'inline-grid',
    gridTemplateAreas: `'${rootGridTemplateAreas.join(' ')}'`,
    gridTemplateColumns: `repeat(3, auto) max-content auto`,
    gridTemplateRows: 'auto',
    maxWidth: `${MAX_STATUS_LABEL_WIDTH}px`,
    padding: theme.spacing(0.5, 1),
    ...makeRootGridTemplateAreaStyles(theme),
  },
});

const makeSharedStyles = (theme: Theme) => ({
  [`.${StatusLabelClassNames.STATUS_ICON}`]: {
    fontSize: theme.spacing(2),
  },

  [`.${StatusLabelClassNames.DOT}`]: {
    fill: theme.palette.common.gray800,
    fontSize: theme.spacing(0.75),
  },

  [`.${StatusLabelClassNames.PRIORITY_LABEL_TEXT}`]: {
    maxWidth: `${maxPriorityTextWidth}px`,
  },

  [`.${StatusLabelClassNames.ACTION_ICON}`]: {
    fontSize: theme.spacing(1),
  },
});

const makeMenuStyles = (theme: Theme) => ({
  [`.${StatusLabelClassNames.MENU_PAPER}`]: {
    border: theme.customBorders.gray300,
    boxShadow: theme.customShadows.dropdown,
    boxSizing: 'border-box',
    margin: theme.spacing(0.25, 0),
    maxWidth: 'calc(100% - 2rem)',
  },

  [`.${StatusLabelClassNames.MENU_CONTAINER}`]: {
    borderRadius: theme.misc.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: `calc(${MAX_STATUS_LABEL_WIDTH}px - 26px)`,
    padding: theme.spacing(1.5),
  },

  [`.${StatusLabelClassNames.MENU_ACTIONS}`]: {
    '& > *': {
      color: theme.palette.primary.main,
    },

    '&:hover': {
      backgroundColor: theme.palette.common.gray100,
    },

    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    margin: theme.spacing(-0.75, -1.5),
    padding: theme.spacing(0.75, 1.5),
  },
});

const useStyles = makeStyles(
  theme => ({
    '@global': {
      [`.${StatusLabelClassNames.LABEL}`]: {
        color: theme.palette.common.gray800,
      },
      ...makeSharedStyles(theme),
      ...makeRootGrid(theme),
      ...makeLabelStyles(theme),
      ...makeMenuStyles(theme),
    },
  }),
  {
    index: -1000, // inject this style after others to give higher priority
    name: 'FountainStatusLabel',
  },
);

export const StatusLabelStyle: FC = () => {
  useStyles();
  return null;
};
